import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateObject } from './create-object'
import { CreateTuple } from './create-tuple'

/**
 * Retrieves the value of a field/element on an object or list
 *
 * Be carefuly to avoid circular dependencies
 *
 * @param reference Key (i.e. field name of object or element position of list)
 * or tuple of [ object, field name ] or [ list, element position ]
 * @example
 * // Referencing `field1` on the current object
 * {
 *   '{}': {
 *     field1: { '@.': 'sqft' },
 *     field2: { '+': [ { '@>': 'field1' }, 100 ] }
 *   }
 * }
 * // ->
 * { field1: 150, field2: 250 }
 *
 * // Referencing the 3rd element (position 2) of the current list
 * {
 *   '[]': [
 *     { '@.': 'id' },
 *     { '@.': 'name' },
 *     { '@.': 'sqft' },
 *     { '@>': 2 }
 *   ]
 * }
 * // ->
 * [ 'abc123', 'Room 100', 150, 150 ]
 *
 * // Referencing a dynamic field on a given object
 * {
 *   id: { '@.': 'id' },
 *   level: {
 *     '@>': [
 *       {
 *         '{}': {
 *           '!A': 1,
 *           '!B': 1,
 *           '!C': 2,
 *           '!D': 3,
 *           '!E': 5,
 *         }
 *       },
 *       { '@.': 'id' }
 *     ]
 *   }
 * }
 * // ->
 * { id: 'A', level: 1 },
 * { id: 'B', level: 1 },
 * { id: 'D', level: 3 },
 * ...
 *
 * // Referencing a dynamic position of a given list
 * {
 *   level: { '@.': 'level' },
 *   relevantAmount: {
 *     '@>': [
 *       {
 *         '[]': [ 0, { '@.': amount }, 0, 0, { '@.': amount }, 0, 0, 0 ],
 *       },
 *       { '@.': 'level' }
 *     ]
 *   }
 * }
 * // ->
 * { level: 1, relevantAmount: 123 },
 * { level: 1, relevantAmount: 234 },
 * { level: 3, relevantAmount: 0 },
 * { level: 4, relevantAmount: 345 },
 * { level: 5, relevantAmount: 0 },
 * ...
 *
 * // TODO: referencing multiple keys to create objects or lists
 */
export interface GetProperty<out T = any> {
  /** {@link GetProperty} */
  readonly [Key.GetProperty]: // Reference static field in the closest containing CreateObject expression
  | string
    // Reference static position in the closest containing CreateTuple expression
    | number
    // Reference dynamic field in specified object
    | readonly [Expression<Object.Constraint>, Expression<string>]
    // Reference dynamic position in specified list
    | readonly [Expression<unknown[]>, Expression<number>]
    // Subset using dynamic fields on specified object
    | (T extends Object.Constraint
        ? readonly [
            Expression<Object.Constraint>,
            readonly Expression<string>[],
          ]
        : never)
    // Subset using dynamic positions on specified list
    | (T extends readonly unknown[]
        ? readonly [Expression<unknown[]>, readonly Expression<number>[]]
        : never)
}
export namespace GetProperty {
  export const isGetProperty: TypeChecker<GetProperty> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<GetProperty>({
        [Key.GetProperty]: TypeChecker.isAny, // TODO:
      }),
    ).usingAlias('Fn.GetProperty')
  export const assertIsGetProperty: TypeAsserter<GetProperty> =
    TypeAsserter.Factory.from(isGetProperty)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.GetProperty,
    {
      readonly target: expressionNode.Node
      /**
       * If defined, the value of this property on {@link target} is desired
       *
       * Exactly one of this and {@link properties} will be defined
       */
      readonly property?: expressionNode.Node
      /**
       * If defined, the subset of {@link target} containing these properties is
       * desired
       *
       * Exactly one of this and {@link property} will be defined
       */
      readonly properties?: readonly expressionNode.Node[]
    }
  > {
    public get type(): common.value.types.Type | undefined {
      if (this.arg.properties) {
        // TODO: we might be able to create a structure if some of the properties are
        // value nodes
        return common.value.types.Intrinsic.Object
      } else if (
        this.arg.property &&
        common.value.types.Structure.isStructure(this.arg.target.type)
      ) {
        if (expressionNode.ForValue.isForValue(this.arg.property)) {
          // If we know what property we are getting, we can get the type for
          // that property

          // NOTE: we don't know what the property value is, but it should be a
          // key (i.e. string or number). If not, then there is a mistake in the
          // query. The casting below is to represent this, and if there is a
          // mistake in the query, then this may just throw an error
          return this.arg.target.type.getProperty(
            this.arg.property.value as string | number,
          )?.type
        } else if (this.arg.target.type.allPropertyTypes.length === 1) {
          // Otherwise, we only know the property type if there is only one type
          // in the structure
          return this.arg.target.type.allPropertyTypes[0]
        }
      }
      return undefined
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.GetProperty, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<GetProperty> = {
    createNode: (fn, context) => {
      const arg = fn[Key.GetProperty]

      if (TypeChecker.isReadonlyArray(arg)) {
        const [targetExpression, propertyExpressionOrExpressions] = arg
        const target = expressionNode.from(
          targetExpression as Expression.Flatten<typeof targetExpression>,
          context,
        )
        let property: expressionNode.Node | undefined
        let properties: readonly expressionNode.Node[] | undefined
        if (TypeChecker.isReadonlyArray(propertyExpressionOrExpressions)) {
          properties = propertyExpressionOrExpressions.map(propertyExpression =>
            expressionNode.from(
              propertyExpression as Expression.Flatten<
                typeof propertyExpression
              >,
              context,
            ),
          )
          // TODO: in theory, we could probably optimize when only some of the
          // properties are values to still simplify for those properties. For
          // example, { "@>": [{ "{}": { "a": 1, "b": 2, "c": 3 } }, [ "a", "b",
          // { "@.": "!attr" } ] ] }
          if (properties.every(expressionNode.ForValue.isForValue)) {
            const keys = properties.map(p => p.value?.toString() ?? 'undefined')

            if (expressionNode.ForValue.isForValue(target)) {
              // If target and all properties are defined, we can produce the
              // final value

              // NOTE: casting just to avoid linting errors. In reality, we are
              // just hoping the query was written in a valid way
              const source = (target.value as Unknown) ?? {}
              /**
               * Duplicate of {@link source}, but only containing the desired
               * properties
               */
              const result = (
                TypeChecker.isReadonlyArray(source) ? [] : {}
              ) as typeof source

              for (const key of keys) {
                result[key] = source[key]
              }

              return new expressionNode.ForValue(result)
            }

            // If the target is a CreateObject node, we can simplify to create
            // an object with only the desired properties
            if (target instanceof CreateObject.Node) {
              const result: Record<string, expressionNode.Node> = {}
              for (const key of keys) {
                result[key] = target.arg[key]
              }
              return new CreateObject.Node(result)
            }

            // If the target is a CreateTuple node, we can simplify to create a
            // tuple with only the desired properties
            if (target instanceof CreateTuple.Node) {
              return new CreateTuple.Node(
                keys.map(
                  // NOTE: JavaScript allows you to index an array/tuple with a
                  // string (i.e. tuple[1] and tuple["1"] are the same), so it is
                  // okay that `key` is a string, but TypeScript doesn't like it,
                  // hence the casting
                  key => target.arg[key satisfies string as unknown as number],
                ),
              )
            }
          }
        } else {
          property = expressionNode.from(
            propertyExpressionOrExpressions as Expression.Flatten<
              typeof propertyExpressionOrExpressions
            >,
            context,
          )
          if (expressionNode.ForValue.isForValue(property)) {
            const key = property.value?.toString() ?? 'undefined'

            if (expressionNode.ForValue.isForValue(target)) {
              // If target and property are both defined, we can select the
              // final value
              return new expressionNode.ForValue(
                (target.value as Unknown)?.[key],
              )
            }
            if (
              target instanceof CreateObject.Node ||
              target instanceof CreateTuple.Node
            ) {
              // If the target is a CreateObject or CreateTuple node, we can
              // skip creating the object/tuple and just get the expression node
              // for the desired property instead

              // NOTE: JavaScript allows you to index an array/tuple with a
              // string (i.e. tuple[1] and tuple["1"] are the same), so it is
              // okay that `key` is a string, but TypeScript doesn't like it,
              // hence the casting
              return target.arg[key satisfies string as string & number]
            }
          }
        }

        return new Node({ properties, property, target })
      }

      const siblingNode = context.siblingExpressionNodes[arg]
      TypeAsserter.assertIsNotNullish(siblingNode)
      return siblingNode
    },
    expressionsInArgs: fn => {
      const arg = fn[Key.GetProperty]
      if (!TypeChecker.isReadonlyArray(arg)) {
        return []
      }
      // There should be two arguments. The second could be an expression or
      // list of expressions. Using `flat()` takes care of that
      return arg.flat()
    },
    typeChecker: isGetProperty,
  }
}
